/* =============================================================
   StudentDocuments.jsx — Student document upload screen
   -------------------------------------------------------------
   Allows the student to upload exchange documents and track
   their review status (pending / approved / rejected by IO).

   Data sources:
     useStudentContext()        — resolves the active student
     useProcessConfig()         — required document list from
                                  the IO's configured process steps
     useStudentDocuments()      — actual uploads from Supabase Storage

   The required document list is dynamic: it is built from the
   process_steps rows that have a document_type_id assigned.
   When the IO adds or removes document requirements in Settings,
   this page updates automatically.
   ============================================================= */

import { useRef, useState, useCallback } from 'react';
import StudentLayout            from '../../components/StudentLayout';
import { useStudentContext }    from '../../hooks/useStudentContext';
import { useProcessConfig }    from '../../hooks/useProcessConfig';
import { useStudentTemplate }  from '../../hooks/useStudentTemplate';
import { useStudentDocuments } from '../../context/DocumentContext';
import { useTaskCompletions }  from '../../hooks/useTaskCompletions';
import { getStudentCategory, buildDocList } from '../../lib/studentCategory';
import { supabase } from '../../lib/supabase';


/* ─── STATUS BADGE CONFIG ───────────────────────────────────── */

function statusConfig(status) {
  switch (status) {
    case 'uploaded':
    case 'approved':
      return { label: 'Approved',           style: 'bg-emerald-100 text-emerald-700', icon: 'check_circle'    };
    case 'pending':
      return { label: 'Pending Review',     style: 'bg-amber-100 text-amber-700',    icon: 'hourglass_empty' };
    case 'rejected':
      return { label: 'Changes Requested',  style: 'bg-red-100 text-red-600',        icon: 'cancel'          };
    case 'not_required':
      return { label: 'Not Required',       style: 'bg-slate-100 text-slate-500',    icon: 'remove_circle'   };
    default:
      return { label: 'Missing',            style: 'bg-red-100 text-red-700',        icon: 'cancel'          };
  }
}

/* ─── Skeleton ──────────────────────────────────────────────── */
function DocRowSkeleton() {
  return (
    <div className="flex items-center justify-between px-4 py-3.5 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex-shrink-0" />
        <div className="space-y-1.5">
          <div className="h-3.5 bg-slate-100 rounded w-32" />
          <div className="h-2.5 bg-slate-100 rounded w-20" />
        </div>
      </div>
      <div className="h-6 bg-slate-100 rounded-full w-24" />
    </div>
  );
}


/* ─── STUDENT DOCUMENTS COMPONENT ──────────────────────────── */

export default function StudentDocuments() {

  /* ── Resolve active student ─────────────────────────────── */
  const { studentId, student, loading: studentLoading } = useStudentContext();

  /* ── Resolve template, then load process steps ─────────── */
  const { templateId } = useStudentTemplate(student);
  const { steps, loading: stepsLoading } = useProcessConfig(templateId);

  /* ── Uploaded documents from Supabase ───────────────────── */
  const {
    documents,
    docsLoading,
    uploadDocument,
  } = useStudentDocuments(studentId);

  /* ── Task completions ────────────────────────────────────── */
  const { completions, toggleTask } = useTaskCompletions(studentId);

  const fileRefs  = useRef({});
  const [uploading,          setUploading]          = useState(null);
  const [uploadError,        setUploadError]        = useState(null);
  const [templateDownloading, setTemplateDownloading] = useState(null); // docName being downloaded

  /* ── Download template handler ──────────────────────────── */
  const handleTemplateDownload = useCallback(async (doc) => {
    if (!doc.template_storage_path) return;
    setTemplateDownloading(doc.name);
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.template_storage_path, 3600);
      if (error || !data?.signedUrl) throw error ?? new Error('Could not generate URL');

      const response = await fetch(data.signedUrl);
      const blob     = await response.blob();
      const blobUrl  = URL.createObjectURL(blob);
      const a        = document.createElement('a');
      a.href         = blobUrl;
      a.download     = doc.template_file_name ?? 'template';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error('[StudentDocuments] template download failed:', err);
    } finally {
      setTemplateDownloading(null);
    }
  }, []);

  // Mostra lo skeleton SOLO al primo caricamento assoluto (nessun dato ancora).
  // Se abbiamo già dati (anche parziali), li mostriamo subito e aggiorniamo
  // in background — evita che il token refresh di Supabase faccia sparire tutto.
  const isLoading = (studentLoading && !student)
                 || (stepsLoading   && steps.length === 0)
                 || (docsLoading    && documents.length === 0 && !!studentId);


  /* ── Build document list from process config ────────────── */
  // Uses buildDocList() which supports multiple documents per step
  // (via process_step_documents junction table).
  // Filtered by the student's category (exchange_type + eu_status).
  const studentCategory = getStudentCategory(student?.exchange_type, student?.eu_status);
  const docs = buildDocList(steps, documents, studentCategory, completions);

  const approvedCount = docs.filter(d =>
    ['uploaded', 'approved', 'task_done'].includes(d.effectiveStatus)
  ).length;
  const progress = docs.length > 0
    ? Math.round((approvedCount / docs.length) * 100)
    : 0;


  /* ── Upload handler ─────────────────────────────────────── */
  const handleUpload = async (docName, file) => {
    if (!file || !studentId) return;
    setUploading(docName);
    setUploadError(null);
    try {
      await uploadDocument(docName, file);
    } catch (err) {
      // Show the error inline rather than crashing the whole page.
      const msg = err?.message ?? err?.error_description ?? JSON.stringify(err);
      setUploadError({ docName, message: msg });
    } finally {
      setUploading(null);
    }
  };


  return (
    <StudentLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">My Documents</h1>
        <p className="text-[#464555] mt-1">Upload and track all your exchange documents.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* Documents list */}
        <div className="col-span-8 space-y-6">

          {/* Progress bar */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            {isLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-slate-100 rounded w-32" />
                  <div className="h-4 bg-slate-100 rounded w-24" />
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full w-full" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-[#0b1c30]">Overall progress</p>
                  <span className="text-sm font-bold text-indigo-600">
                    {approvedCount} of {docs.length} complete
                  </span>
                </div>
                <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {docs.filter(d => d.effectiveStatus === 'pending').length > 0
                    ? `${docs.filter(d => d.effectiveStatus === 'pending').length} document(s) waiting for IO approval`
                    : `${docs.filter(d => !['uploaded','approved','task_done','not_required'].includes(d.effectiveStatus)).length} item(s) still need your attention`
                  }
                </p>
              </>
            )}
          </div>

          {/* Required Documents */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">folder_open</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Required Documents</h2>
            </div>
            <div className="p-4 space-y-2">
              {isLoading ? (
                <>
                  <DocRowSkeleton />
                  <DocRowSkeleton />
                  <DocRowSkeleton />
                </>
              ) : docs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  No documents required yet. Your IO manager will configure the requirements.
                </p>
              ) : (
                docs.map((doc) => {
                  const cfg = statusConfig(doc.effectiveStatus);
                  const canUpload = doc.uploadable &&
                    !['pending', 'uploaded', 'approved'].includes(doc.effectiveStatus);
                  const isPending = doc.effectiveStatus === 'pending';

                  return (
                    <div
                      key={doc.name}
                      className="flex items-center justify-between px-4 py-3.5 rounded-xl transition-colors group hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <span className="material-symbols-outlined text-[18px] text-slate-400">
                            {doc.icon}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#0b1c30]">{doc.name}</p>
                          {isPending && (
                            <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                              <span className="material-symbols-outlined text-[12px]">schedule</span>
                              Uploaded · waiting for IO review
                            </p>
                          )}
                          {doc.effectiveStatus === 'rejected' && (
                            <p className="text-xs text-red-500 mt-0.5">
                              Changes requested — please re-upload
                            </p>
                          )}
                          {canUpload && (
                            <p className="text-xs text-slate-400 mt-0.5">Upload required</p>
                          )}
                          {isPending && doc.upload && (
                            <p className="text-xs text-slate-400 mt-0.5">{doc.upload.fileName}</p>
                          )}
                          {/* Deadline indicator */}
                          {doc.deadline_date && (() => {
                            const isComplete = ['uploaded','approved','task_done'].includes(doc.effectiveStatus);
                            return (
                              <p className={`text-xs mt-0.5 flex items-center gap-1 font-medium ${
                                isComplete                                              ? 'text-emerald-500' :
                                doc.deadlineDays !== null && doc.deadlineDays <= 7    ? 'text-red-500'     :
                                doc.deadlineDays !== null && doc.deadlineDays <= 14   ? 'text-amber-500'   :
                                'text-slate-400'
                              }`}>
                                <span className="material-symbols-outlined text-[12px]">event</span>
                                Due {new Date(doc.deadline_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {!isComplete && (
                                  <>
                                    {doc.deadlineDays !== null && doc.deadlineDays <= 0  && ' · Overdue'}
                                    {doc.deadlineDays !== null && doc.deadlineDays === 1 && ' · Tomorrow'}
                                    {doc.deadlineDays !== null && doc.deadlineDays > 1 && doc.deadlineDays <= 14 && ` · ${doc.deadlineDays} days left`}
                                  </>
                                )}
                              </p>
                            );
                          })()}
                          {uploadError?.docName === doc.name && (
                            <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                              <span className="material-symbols-outlined text-[12px]">error</span>
                              {uploadError.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {doc.item_type === 'task' ? (
                          /* Task checkbox */
                          <button
                            onClick={() => toggleTask(doc.junctionId)}
                            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                              doc.effectiveStatus === 'task_done'
                                ? 'bg-emerald-100 border-emerald-200 text-emerald-700'
                                : 'bg-white border-slate-200 text-slate-500 hover:border-amber-300 hover:text-amber-600'
                            }`}
                          >
                            <span className="material-symbols-outlined text-[15px]">
                              {doc.effectiveStatus === 'task_done' ? 'check_box' : 'check_box_outline_blank'}
                            </span>
                            {doc.effectiveStatus === 'task_done' ? 'Done' : 'Mark as done'}
                          </button>
                        ) : (
                          <>
                            {/* Download Template button */}
                            {doc.template_storage_path && (
                              <button
                                onClick={() => handleTemplateDownload(doc)}
                                disabled={templateDownloading === doc.name}
                                className="text-xs font-medium text-indigo-600 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                                title="Download the template, fill it out, then upload it"
                              >
                                {templateDownloading === doc.name ? (
                                  <><span className="material-symbols-outlined text-[13px] animate-spin">progress_activity</span>Downloading…</>
                                ) : (
                                  <><span className="material-symbols-outlined text-[13px]">download</span>Template</>
                                )}
                              </button>
                            )}
                            {/* Status badge */}
                            <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.style}`}>
                              <span className="material-symbols-outlined text-[14px]">{cfg.icon}</span>
                              {cfg.label}
                            </div>
                            {/* Upload / re-upload button */}
                            {(canUpload || doc.effectiveStatus === 'rejected') && (
                              <>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  className="hidden"
                                  ref={el => fileRefs.current[doc.name] = el}
                                  onChange={e => handleUpload(doc.name, e.target.files[0])}
                                />
                                <button
                                  onClick={() => fileRefs.current[doc.name]?.click()}
                                  disabled={uploading === doc.name}
                                  className="text-xs font-medium text-indigo-600 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                                >
                                  {uploading === doc.name ? (
                                    <><span className="material-symbols-outlined text-[13px] animate-spin">progress_activity</span>Uploading…</>
                                  ) : (
                                    <><span className="material-symbols-outlined text-[13px]">upload</span>{doc.effectiveStatus === 'rejected' ? 'Re-upload' : 'Upload'}</>
                                  )}
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>

        {/* Right column */}
        <div className="col-span-4 space-y-6">

          {/* Upload tip */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-indigo-500 text-[20px]">info</span>
              <p className="text-sm font-semibold text-indigo-800">Upload instructions</p>
            </div>
            <p className="text-xs text-indigo-600 leading-relaxed">
              Upload PDF or image files. Max 50 MB per file. Documents are reviewed by your IO manager.
            </p>
          </div>

          {/* Need help */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-5">
              <p className="text-sm font-semibold text-[#0b1c30] mb-1">Need help?</p>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                Contact your IO manager if you have trouble uploading documents.
              </p>
              <a
                href="mailto:j.klein@esmt.org"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#4343d5] text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">mail</span>
                Email IO Manager
              </a>
            </div>
          </div>

        </div>
      </div>
    </StudentLayout>
  );
}
